import React from 'react'
import { Button, Dropdown } from 'antd'
import type { MenuProps } from 'antd'

import EllipsisButton from '../common/EllipsisButton'

import type { LinkedAccountItem } from '../../types/linkedAccount'
import { accountTypeBadgeLabel, customTypeLine } from '../../util/linkedAccount/linkedAccountDisplay'

/**
 * Thẻ MỘT bản ghi tài khoản liên kết (SP-005, Task 3) — Figma desktop `26:7691`, responsive `26:44527`.
 *
 * 🔴 KHÔNG BAO GIỜ hiển thị mật khẩu dưới bất kỳ dạng nào (kể cả ký tự che) — `LinkedAccountItem`
 * (Task 2) không có trường mật khẩu, nên component này không có gì để lộ nếu không tự thêm vào.
 *
 * 🔴 Fix round 2 (17/09/2026, quyết định của designer, GHI ĐÈ ruling trước đó): cụm nút bên phải
 * KHÔNG BAO GIỜ hiện cả hai nút cùng lúc — mỗi view chỉ có ĐÚNG MỘT nút, không view nào có 0 nút:
 * - `canManage === true` (Quản lý tài khoản → Chi tiết tài khoản, nhân viên vận hành) ⇒ CHỈ nút `…`.
 *   Vận hành cố tình KHÔNG được xem thẳng mật khẩu từ danh sách; cần thì mở `Chỉnh sửa`, hộp đó có
 *   nút `Xem mật khẩu` riêng sau bước xác thực lại.
 * - `canManage === false` (Tài khoản của tôi, xem bản ghi của chính mình) ⇒ CHỈ nút `Nhận mật khẩu`.
 *   Màn chỉ đọc, mật khẩu là của chính người xem, không có menu `…`.
 *
 * DOM hook cho các task sau / bộ test trình duyệt:
 * - `aria-label="Thao tác"` trên nút mở menu `…` (chỉ render khi `canManage === true`).
 * - Nút "Nhận mật khẩu" chỉ render khi `canManage === false`.
 * - `data-testid="linked-account-note"` trên khối nội dung Ghi chú (luôn render, kể cả rỗng).
 * - `.linked-account-card__badge--vinaphone|viettel|other` cho màu badge theo loại.
 */

export interface LinkedAccountCardProps {
  item: LinkedAccountItem
  /**
   * `true` ⇒ chỉ hiện menu `…` (Chỉnh sửa/Xoá), KHÔNG hiện "Nhận mật khẩu" — màn "Quản lý tài
   * khoản → Chi tiết tài khoản" của nhân viên vận hành.
   * `false` ⇒ chỉ hiện "Nhận mật khẩu", KHÔNG hiện menu `…` — màn "Tài khoản của tôi" (xem bản ghi
   * của chính mình) và với role không có quyền MANAGE.
   */
  canManage: boolean
  onRevealPassword: (item: LinkedAccountItem) => void
  onEdit: (item: LinkedAccountItem) => void
  onDelete: (item: LinkedAccountItem) => void
}

const MENU_KEY_EDIT = 'edit'
const MENU_KEY_DELETE = 'delete'

const ACTION_MENU_LABEL = 'Thao tác'
const REVEAL_PASSWORD_LABEL = 'Nhận mật khẩu'
const EDIT_LABEL = 'Chỉnh sửa'
const DELETE_LABEL = 'Xoá'
const USERNAME_LABEL = 'Tên tài khoản:'
const CUSTOM_TYPE_LABEL = 'Loại tài khoản:'
const NOTE_LABEL = 'Ghi chú'

// "Xoá" là thao tác phá huỷ ⇒ `danger` (Figma `26:8137` tô chữ đỏ `#e92c2c`, màu chỉnh ở SCSS).
const ACTION_MENU_ITEMS: NonNullable<MenuProps['items']> = [
  { key: MENU_KEY_EDIT, label: EDIT_LABEL },
  { key: MENU_KEY_DELETE, label: DELETE_LABEL, danger: true },
]

const LinkedAccountCard = ({
  item, canManage, onRevealPassword, onEdit, onDelete,
}: LinkedAccountCardProps): JSX.Element => {
  const customLine = customTypeLine(item)

  const handleMenuClick: NonNullable<MenuProps['onClick']> = ({ key }) => {
    if (key === MENU_KEY_EDIT) onEdit(item)
    else if (key === MENU_KEY_DELETE) onDelete(item)
  }

  return (
    <div className="linked-account-card">
      {/* Figma `26:7878`: hai khối xếp dọc cách 12 — khối đầu (hàng badge + các dòng thông tin,
          cách nhau 8) và khối ghi chú (nhãn → nội dung cách 4). */}
      <div className="linked-account-card__top">
        <div className="linked-account-card__header">
          <span
            className={`linked-account-card__badge linked-account-card__badge--${item.account_type}`}
          >
            {accountTypeBadgeLabel(item)}
          </span>
          <div className="linked-account-card__actions">
            {!canManage && (
            <Button
              size="large"
              className="linked-account-card__reveal-btn"
              onClick={() => onRevealPassword(item)}
            >
              {REVEAL_PASSWORD_LABEL}
            </Button>
            )}
            {canManage && (
            <Dropdown
              menu={{ items: ACTION_MENU_ITEMS, onClick: handleMenuClick }}
              trigger={['click']}
              placement="bottomRight"
              // Popup render qua portal ⇒ phải gắn class lên chính popup để SCSS với tới (Figma `26:8137`).
              overlayClassName="linked-account-card__menu-dropdown"
              // 🔴 Fix round 1 (18/09/2026, Chrome thật): thêm/xoá bản ghi refetch danh sách ⇒ thẻ
              // này unmount trong khi menu vẫn còn mở/đang đóng dần. antd giữ popup của Dropdown
              // (Overflow + ResizeObserver bên trong) SỐNG QUA lần unmount trừ khi bảo nó tự huỷ —
              // không có prop này thì ResizeObserver của popup còn "Can't perform a React state
              // update on an unmounted component" sau khi thẻ đã biến mất khỏi DOM.
              destroyPopupOnHide
            >
              <EllipsisButton aria-label={ACTION_MENU_LABEL} className="linked-account-card__menu-btn" />
            </Dropdown>
            )}
          </div>
        </div>
        {customLine !== null && (
          <div className="linked-account-card__custom-type">
            <span className="linked-account-card__label">{CUSTOM_TYPE_LABEL}</span>
            {' '}
            <span className="linked-account-card__value">{customLine}</span>
          </div>
        )}
        <div className="linked-account-card__username">
          <span className="linked-account-card__label">{USERNAME_LABEL}</span>
          {' '}
          <span className="linked-account-card__value">{item.username}</span>
        </div>
      </div>
      <div className="linked-account-card__note-section">
        <div className="linked-account-card__note-label">{NOTE_LABEL}</div>
        <div
          className="linked-account-card__note"
          data-testid="linked-account-note"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {item.note ?? ''}
        </div>
      </div>
    </div>
  )
}

export default LinkedAccountCard
